import { useEffect, useState } from "react"
import supabase from "../supabaseconfig"
import { Recommendation } from "../types/supabase"
import style from "./anbefalinger.module.css"
import { AnbefalingType, visningstekstForKategori } from "./utils"

function hentIkon(type?: string): string {
    switch (type) {
        case AnbefalingType.RESTAURANT:
            return "🍽️"
        case AnbefalingType.MUSEUM:
            return "🏛️"
        case AnbefalingType.BAR:
            return "🍸"
        case AnbefalingType.AKTIVITET:
            return "🥾"
        case AnbefalingType.STRAND:
            return "🏖️"
        default:
            return "❓"
    }
}

export function AnbefalingModal({
    data,
    lukk,
    vis,
    bruker,
    onRediger,
    forceOverlay = false,
}: {
    data?: Recommendation
    lukk: () => void
    vis: boolean
    bruker?: string
    onRediger?: (data: Recommendation) => void
    forceOverlay?: boolean
}) {
    const [signedUrls, setSignedUrls] = useState<string[]>([])
    const [aktivtBilde, setAktivtBilde] = useState<number | null>(null)

    useEffect(() => {
        const isMobile = window.matchMedia("(max-width: 50em)").matches
        if (vis && (isMobile || forceOverlay)) {
            document.body.style.overflow = "hidden"
            return () => {
                document.body.style.overflow = ""
            }
        }
    }, [vis, forceOverlay])

    useEffect(() => {
        if (!vis || !data?.photo_urls?.length) {
            setSignedUrls([])
            return
        }

        async function hentSignerteUrler() {
            const { data: signedData, error } = await supabase()
                .storage.from("recommendation-photos")
                .createSignedUrls(data!.photo_urls!, 3600)

            if (error) {
                console.error("Feil ved henting av signerte URLer:", error)
                return
            }

            setSignedUrls(signedData.map((item) => item.signedUrl))
        }

        hentSignerteUrler()
    }, [vis, data])

    useEffect(() => {
        if (aktivtBilde === null) return
        function handleKeyDown(e: KeyboardEvent) {
            if (e.key === "Escape") setAktivtBilde(null)
            if (e.key === "ArrowLeft")
                setAktivtBilde(
                    (prev) =>
                        (prev! - 1 + signedUrls.length) % signedUrls.length,
                )
            if (e.key === "ArrowRight")
                setAktivtBilde((prev) => (prev! + 1) % signedUrls.length)
        }
        window.addEventListener("keydown", handleKeyDown)
        return () => window.removeEventListener("keydown", handleKeyDown)
    }, [aktivtBilde, signedUrls?.length])

    if (!vis || !data) return null
    return (
        <div
            className={forceOverlay ? style.kartModalOverlay : style.anbefalingModalOverlay}
            onClick={lukk}
        >
            <div
                className={`${style.anbefalingModal} ${forceOverlay ? style.kartModal : ""}`}
                onClick={(e) => e.stopPropagation()}
            >
                <div className={style.modalToppKnapper}>
                    {bruker && data.user === bruker && onRediger && (
                        <button
                            className={style.redigerModalKnapp}
                            type="button"
                            title="Rediger"
                            onClick={() => onRediger(data)}
                        >
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M11.5 1.5l3 3L5 14H2v-3L11.5 1.5z" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                            Rediger
                        </button>
                    )}
                    <button className={style.lukk} onClick={lukk}>
                        ✕
                    </button>
                </div>
                <span className={style.kategoriTag} data-type={data.type}>
                    {hentIkon(data.type)}{" "}
                    {visningstekstForKategori(data.type as AnbefalingType)}
                </span>
                <h2 className={style.modalTittel}>{data.name}</h2>

                <div className={style.anbefalingInnhold}>
                    <div className={style.modalInfo}>
                        <div className={style.modalFelt}>
                            <span className={style.modalLabel}>
                                Anbefalt av
                            </span>
                            <span>{data.user}</span>
                        </div>
                        {data.rating && (
                            <div className={style.modalFelt}>
                                <span className={style.modalLabel}>Rating</span>
                                <span>⭐ {data.rating}</span>
                            </div>
                        )}
                        <div className={style.modalFelt}>
                            <span className={style.modalLabel}>
                                Beskrivelse
                            </span>
                            <span>{data.description}</span>
                        </div>
                        {data.address && (
                            <div className={style.modalFelt}>
                                <span className={style.modalLabel}>
                                    Adresse
                                </span>
                                <span>{data.address}</span>
                            </div>
                        )}
                        {data.url && (
                            <a className={"url"} href={data.url}>
                                Nettside
                            </a>
                        )}
                    </div>
                    <div>
                        <h3 className={style.modalLabel}>Bilder</h3>
                        {signedUrls.length > 0 ? (
                            <div className={style.bildeGalleri}>
                                {signedUrls.map((url, index) => (
                                    <img
                                        key={index}
                                        src={url}
                                        alt={`Bilde ${index + 1}`}
                                        className={style.galleriBilde}
                                        onClick={() => setAktivtBilde(index)}
                                    />
                                ))}
                            </div>
                        ) : (
                            <p className={style.anbefalingDetaljer}>
                                Ingen bilder lagt til
                            </p>
                        )}
                    </div>
                </div>

                {aktivtBilde !== null && (
                    <div
                        className={style.lightboxOverlay}
                        onClick={() => setAktivtBilde(null)}
                    >
                        <button
                            className={style.lightboxLukk}
                            onClick={() => setAktivtBilde(null)}
                        >
                            ✕
                        </button>
                        {signedUrls.length > 1 && (
                            <button
                                className={style.lightboxPil}
                                onClick={(e) => {
                                    e.stopPropagation()
                                    setAktivtBilde(
                                        (aktivtBilde - 1 + signedUrls.length) %
                                            signedUrls.length,
                                    )
                                }}
                            >
                                ‹
                            </button>
                        )}
                        <img
                            src={signedUrls[aktivtBilde]}
                            alt={`Bilde ${aktivtBilde + 1}`}
                            className={style.lightboxBilde}
                            onClick={(e) => e.stopPropagation()}
                        />
                        {signedUrls.length > 1 && (
                            <button
                                className={style.lightboxPil}
                                onClick={(e) => {
                                    e.stopPropagation()
                                    setAktivtBilde(
                                        (aktivtBilde + 1) % signedUrls.length,
                                    )
                                }}
                            >
                                ›
                            </button>
                        )}
                    </div>
                )}
            </div>
        </div>
    )
}
